import React from "react";

import { getCurrentPlatformTermsAndConditions } from "services";

import { getContactLink, getIubendaPolicyId, getLanguageFromLocalStorage, openFileInNewTab, useTranslation } from "utils";

import { CODE_OF_CONDUCT_LINK, IMPRINT_LINK, INSTAGRAM_LINK, LINKEDIN_LINK } from "consts";

import { Anchor, Icon, Svg, Typography } from "components";

type FooterLinkProps = {
  href: string;
  target?: string;
  className?: string;
  mixpanelText?: string;
  onClick?: (event: React.MouseEvent) => void;
  children: React.ReactNode;
};

/** Mask SVGs applied via CSS so the bezier cutouts stay pixel-true. */
const FOOTER_MASK_DESKTOP = "/images/soilsight-pre-login/footer-mask.svg";
const FOOTER_MASK_MOBILE = "/images/soilsight-pre-login/footer-mask-mobile.svg";

const FOOTER_MASK_STYLE = {
  "--soilsight-footer-mask": `url("${FOOTER_MASK_DESKTOP}")`,
  "--soilsight-footer-mask-mobile": `url("${FOOTER_MASK_MOBILE}")`,
} as React.CSSProperties;

const SOCIAL_LINKS = [
  { key: "instagram", href: INSTAGRAM_LINK, iconName: "tabler/brand-instagram" },
  { key: "linkedin", href: LINKEDIN_LINK, iconName: "tabler/brand-linkedin" },
] as const;

// Both breakpoint copies always render; CSS reveals the one that fits.
const BREAKPOINTS = ["desktop", "mobile"] as const;

const HQ_ADDRESS_BY_BREAKPOINT = {
  desktop: "Klim GmbH\nSchwedter Straße 263\n10119 Berlin",
  mobile: "Klim GmbH\nSchwedter\nStraße 263\n10119 Berlin",
} as const;

const PRIVACY_LABEL_KEY_BY_BREAKPOINT = {
  desktop: "dataProtection",
  mobile: "dataSecurity",
} as const;

const openTermsAndConditions = async (event: React.MouseEvent) => {
  event.preventDefault();
  const termsDocument = await getCurrentPlatformTermsAndConditions();
  openFileInNewTab(termsDocument);
};

const getPrivacyPolicyHref = () => `https://www.iubenda.com/privacy-policy/${getIubendaPolicyId(getLanguageFromLocalStorage())}`;

const FooterLink = ({ children, ...anchorProps }: FooterLinkProps) => (
  <li>
    <Anchor {...anchorProps}>{children}</Anchor>
  </li>
);

const LegalNavBlock = () => {
  const { t } = useTranslation("scenarioAnalysis");
  const privacyPolicyHref = getPrivacyPolicyHref();

  return (
    <div className="soilsight-pre-login__footer-nav-block">
      <Typography
        variant="paragraph-m"
        color="white"
        className="soilsight-pre-login__footer-section-heading soilsight-pre-login__footer-section-heading--legal"
      >
        {t("preLogin.footer.legalHeading")}
      </Typography>
      <ul className="soilsight-pre-login__footer-nav">
        <FooterLink href={privacyPolicyHref} target="_blank" mixpanelText="soilsight-pre-login-privacy-policy" className="soilsight-pre-login__footer-text">
          {BREAKPOINTS.map((breakpoint) => (
            <span key={breakpoint} className={`soilsight-pre-login__footer-nav-label soilsight-pre-login__footer-nav-label--${breakpoint}`}>
              {t(`preLogin.footer.${PRIVACY_LABEL_KEY_BY_BREAKPOINT[breakpoint]}`)}
            </span>
          ))}
        </FooterLink>
        <FooterLink href={IMPRINT_LINK} target="_blank" className="soilsight-pre-login__footer-text">
          {t("preLogin.footer.legalNotice")}
        </FooterLink>
        <FooterLink href={CODE_OF_CONDUCT_LINK} target="_blank" className="soilsight-pre-login__footer-text">
          {t("preLogin.footer.codeOfConduct")}
        </FooterLink>
        <FooterLink href="/" onClick={openTermsAndConditions} className="soilsight-pre-login__footer-text">
          {t("preLogin.footer.terms")}
        </FooterLink>
      </ul>
    </div>
  );
};

const ContactBlock = () => {
  const { t } = useTranslation("scenarioAnalysis");

  return (
    <div className="soilsight-pre-login__footer-contact">
      <Typography variant="paragraph-m" color="white" className="soilsight-pre-login__footer-section-heading soilsight-pre-login__footer-section-heading--hq">
        {t("preLogin.footer.hqHeading")}
      </Typography>
      <div className="soilsight-pre-login__footer-contact-details">
        <div className="soilsight-pre-login__footer-address">
          <Icon iconName="tabler/map-pin" size="s" className="soilsight-pre-login__footer-contact-icon soilsight-pre-login__footer-contact-icon--top" />
          {BREAKPOINTS.map((breakpoint) => (
            <Typography
              key={breakpoint}
              variant="paragraph-m"
              color="white"
              className={`soilsight-pre-login__footer-text soilsight-pre-login__footer-address-text soilsight-pre-login__footer-address-text--${breakpoint}`}
            >
              {HQ_ADDRESS_BY_BREAKPOINT[breakpoint]}
            </Typography>
          ))}
        </div>
        <Anchor
          href={getContactLink()}
          target="_blank"
          mixpanelText="soilsight-pre-login-contact"
          className="soilsight-pre-login__footer-text soilsight-pre-login__footer-contact-link"
        >
          <Icon iconName="tabler/mail" size="s" className="soilsight-pre-login__footer-contact-icon" />
          {t("preLogin.footer.contact")}
        </Anchor>
      </div>
    </div>
  );
};

const BrandAndSocialsBlock = () => {
  const { t } = useTranslation("scenarioAnalysis");

  return (
    <div className="soilsight-pre-login__footer-bottom">
      <div className="soilsight-pre-login__footer-brand">
        <Svg svgName="logos/klim_logo_text_black" className="soilsight-pre-login__footer-logo" width={56} height={24} />
        <Typography variant="paragraph-m" color="white" className="soilsight-pre-login__footer-text">
          {t("preLogin.footer.copyright", { year: new Date().getFullYear() })}
        </Typography>
      </div>
      <div className="soilsight-pre-login__footer-socials">
        {SOCIAL_LINKS.map(({ key, href, iconName }) => (
          <Anchor key={key} href={href} target="_blank" mixpanelText={`soilsight-pre-login-${key}`}>
            <Icon iconName={iconName} size="m" />
            <span className="soilsight-pre-login__footer-social-label">{t(`preLogin.footer.${key}Label`)}</span>
          </Anchor>
        ))}
      </div>
    </div>
  );
};

export const PreLoginFooter = () => {
  const { t } = useTranslation("scenarioAnalysis");

  return (
    <footer className="soilsight-pre-login__footer soilsight-pre-login__footer--marketing">
      <div className="soilsight-pre-login__footer-panel" style={FOOTER_MASK_STYLE}>
        <Typography variant="paragraph-m" color="white" className="soilsight-pre-login__footer-tagline">
          {t("preLogin.footer.tagline")}
        </Typography>

        <div className="soilsight-pre-login__footer-body">
          <div className="soilsight-pre-login__footer-mid">
            <LegalNavBlock />
            <ContactBlock />
          </div>
          <BrandAndSocialsBlock />
        </div>
      </div>
    </footer>
  );
};
